// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import { type ConduitSizeId, type ConduitTypeId, useEvolvePaginatedGet } from '@evolvemep/api-ts';

import { isNil } from 'helpers/isNotNil';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';

/** Enough to cover every bender profile a company has, so the compatibility check sees all of them. */
const MAX_BENDER_PROFILES = 200;

/**
 * Returns a function to call after the conduit type or size changes. Once both are chosen, it keeps the chosen bender
 * if it can take the conduit, and otherwise silently picks the first bender that can. When none can, it clears the
 * bender, which leaves the Dimensions step not done.
 */
export const useKeepCompatibleBender = () => {
  const { fetchPage: fetchCompatibleProfiles } = useEvolvePaginatedGet('shop/fabrication/machineProfile');
  const { values, setFieldValue } = useBendCreatorFormContext();
  const { scheduleValidation } = useBendCreatorValidationContext();
  const currentProfileId = values.benderProfile?.machineProfileId;

  return useCallback(
    async (conduitTypeId: ConduitTypeId | null, conduitSizeId: ConduitSizeId | null) => {
      if (isNil(conduitTypeId) || isNil(conduitSizeId)) return;
      try {
        const { data: compatibleProfiles } = await fetchCompatibleProfiles(
          { skip: 0, take: MAX_BENDER_PROFILES, throwOnMoreRecent: true },
          { params: { conduitTypeId, conduitSizeId } },
        );
        if (compatibleProfiles.some(({ machineProfileId }) => machineProfileId === currentProfileId)) return;
        setFieldValue('benderProfile', compatibleProfiles[0] ?? null);
        scheduleValidation('benderProfile');
      } catch {
        // Either a newer material change superseded this check, and that check handles the bender instead, or the
        // request failed, which the API hook already reports. Either way the bender is left as it is.
      }
    },
    [currentProfileId, fetchCompatibleProfiles, setFieldValue, scheduleValidation],
  );
};
